import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { PlusCircle, Search, Filter, X, RefreshCw, Hash, Server, MessageCircle, User } from 'lucide-react';
import { Button, Input, Select } from '../components/ui';
import { PredictionList } from '../components/predictions';
import { useUIStore, useServerStore, usePredictionStore, useAuthStore } from '../store';
import { api } from '../lib/api';
import type { Prediction, PredictionStatus } from '../types';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'success', label: 'Success' },
  { value: 'fail', label: 'Failed' },
  { value: 'invalidated', label: 'Invalidated' },
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'deadline', label: 'Deadline' },
  { value: 'confidence', label: 'Confidence' },
  { value: 'votes', label: 'Most Votes' },
];

export function Feed() {
  const { setView } = useUIStore();
  const { currentServer, currentChannel } = useServerStore();
  const { predictions, setPredictions, filter, setFilter, setSelectedPrediction } = usePredictionStore();
  const { isAuthenticated } = useAuthStore();

  const [showFilters, setShowFilters] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const loadPredictions = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!isAuthenticated) {
      console.log('loadPredictions: Not authenticated, skipping');
      setIsLoading(false);
      return;
    }

    if (!currentServer) {
      console.log('loadPredictions: No currentServer, skipping');
      setIsLoading(false);
      return;
    }

    console.log('loadPredictions: Fetching with', {
      serverId: currentServer.id,
      serverName: currentServer.name,
      channelId: currentChannel?.id,
      channelName: currentChannel?.name,
    });

    setIsLoading(true);
    try {
      const response = await api.predictions.list(
        currentServer.id,
        currentChannel?.id
      );
      console.log('loadPredictions: Response', {
        success: response.success,
        count: response.data?.length,
        error: response.error,
      });
      if (response.success && response.data) {
        setPredictions(response.data);
      }
    } catch (error) {
      console.error('Failed to load predictions:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentServer, currentChannel, setPredictions, isAuthenticated]);

  useEffect(() => {
    loadPredictions();
  }, [loadPredictions]);

  const filteredPredictions = useMemo(() => {
    let result = [...predictions];

    if (filter.status !== 'all') {
      result = result.filter((p) => p.status === filter.status);
    }

    if (filter.search) {
      const search = filter.search.toLowerCase();
      result = result.filter(
        (p) =>
          p.text.toLowerCase().includes(search) ||
          p.authorName.toLowerCase().includes(search)
      );
    }

    switch (filter.sortBy) {
      case 'deadline':
        result.sort((a, b) => {
          if (!a.deadline) return 1;
          if (!b.deadline) return -1;
          return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
        });
        break;
      case 'confidence':
        result.sort((a, b) => b.confidence - a.confidence);
        break;
      case 'votes':
        result.sort((a, b) => {
          const aVotes = a.votes.success.length + a.votes.fail.length;
          const bVotes = b.votes.success.length + b.votes.fail.length;
          return bVotes - aVotes;
        });
        break;
      default:
        result.sort((a, b) => {
          const aTime = a.createdAt instanceof Date ? a.createdAt.getTime() : new Date(a.createdAt).getTime();
          const bTime = b.createdAt instanceof Date ? b.createdAt.getTime() : new Date(b.createdAt).getTime();
          return bTime - aTime;
        });
    }

    return result;
  }, [predictions, filter]);

  const handleSelectPrediction = (prediction: Prediction) => {
    setSelectedPrediction(prediction);
    setView('detail');
  };

  // Get channel avatar
  const getChannelAvatar = () => {
    if (!currentChannel) return null;

    const isDM = currentChannel.type === 1 || (!currentChannel.serverId || currentChannel.serverId === 'dm');
    const isGroupDM = currentChannel.type === 3;

    // DM with recipient avatar
    if (isDM && currentChannel.recipientAvatar && currentChannel.recipientId) {
      return (
        <img
          src={`https://cdn.discordapp.com/avatars/${currentChannel.recipientId}/${currentChannel.recipientAvatar}.png?size=64`}
          alt=""
          className="w-14 h-14 rounded-xl"
        />
      );
    }

    // Group DM with icon
    if (isGroupDM && currentChannel.groupIcon) {
      return (
        <img
          src={`https://cdn.discordapp.com/channel-icons/${currentChannel.id}/${currentChannel.groupIcon}.png?size=64`}
          alt=""
          className="w-14 h-14 rounded-xl"
        />
      );
    }

    // Guild channel with server icon
    if (!isDM && !isGroupDM && currentChannel.guildIcon && currentChannel.serverId) {
      return (
        <img
          src={`https://cdn.discordapp.com/icons/${currentChannel.serverId}/${currentChannel.guildIcon}.png?size=64`}
          alt=""
          className="w-14 h-14 rounded-xl"
        />
      );
    }

    // Fallback icons
    if (isDM) {
      return (
        <div className="w-14 h-14 rounded-xl bg-foretell-accent/20 flex items-center justify-center">
          <MessageCircle size={24} className="text-foretell-accent" />
        </div>
      );
    }

    return (
      <div className="w-14 h-14 rounded-xl bg-black/40 flex items-center justify-center">
        <Hash size={24} className="text-slate-400" />
      </div>
    );
  };

  return (
    <div className="animate-in">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mb-8">
        <div className="flex items-start gap-4 flex-grow">
          {getChannelAvatar()}
          <div>
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
              {currentChannel ? `#${currentChannel.name}` : 'All Channels'}
            </div>
            <h2 className="text-2xl font-bold tracking-tight">
              {currentServer?.name || 'Predictions'}
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              {filteredPredictions.length} prediction{filteredPredictions.length !== 1 ? 's' : ''}
            </p>
            <div className="mt-2 p-2 bg-black/30 rounded-lg border border-white/5 inline-block">
              <div className="flex items-center gap-4 text-[10px] text-slate-500">
                <div className="flex items-center gap-1.5">
                  <Server size={12} />
                  <span className="font-mono">{currentServer?.id || 'none'}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Hash size={12} />
                  <span className="font-mono">{currentChannel?.id || 'none'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={loadPredictions}
            icon={<RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />}
          >
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          {isAuthenticated && (
            <Button
              variant="primary"
              onClick={() => setView('create')}
              icon={<PlusCircle size={18} />}
            >
              New Prediction
            </Button>
          )}
        </div>
      </div>

      <div className="mb-6 space-y-4">
        <div className="flex gap-3">
          <div className="relative flex-grow">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30"
            />
            <input
              type="text"
              placeholder="Search predictions..."
              value={filter.search}
              onChange={(e) => setFilter({ search: e.target.value })}
              className="w-full bg-black/40 border border-white/10 rounded-xl pl-11 pr-4 py-3 text-sm placeholder-white/30 focus:outline-none focus:border-foretell-accent transition-colors"
            />
            {filter.search && (
              <button
                onClick={() => setFilter({ search: '' })}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <Button
            variant="secondary"
            onClick={() => setShowFilters(!showFilters)}
            icon={<Filter size={18} />}
            className={showFilters ? 'border-foretell-accent text-foretell-accent' : ''}
          >
            <span className="hidden sm:inline">Filters</span>
          </Button>
        </div>

        {showFilters && (
          <div className="glass-card rounded-xl p-4 grid grid-cols-2 gap-4 animate-slide-down">
            <Select
              label="Status"
              value={filter.status}
              onChange={(e) =>
                setFilter({ status: e.target.value as PredictionStatus | 'all' })
              }
              options={STATUS_OPTIONS}
            />
            <Select
              label="Sort By"
              value={filter.sortBy}
              onChange={(e) =>
                setFilter({
                  sortBy: e.target.value as typeof filter.sortBy,
                })
              }
              options={SORT_OPTIONS}
            />
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-foretell-accent" />
        </div>
      ) : (
        <PredictionList
          predictions={filteredPredictions}
          onSelect={handleSelectPrediction}
          emptyMessage={
            filter.search || filter.status !== 'all'
              ? 'No predictions match your filters'
              : 'No predictions in this channel yet'
          }
        />
      )}
    </div>
  );
}
